import { useEffect, useState } from 'react';
import Reveal from '../../components/Reveal';
import HeroSlideshow from '../../components/HeroSlideshow';
import SafeImage from '../../components/SafeImage';
import './ngorongoro.css';
import {
  IMAGE_FALLBACK,
  NGORONGORO_HERO_SLIDES,
  NGORONGORO_PAGE_IMAGES,
} from '../../data/destinationImages';

import lionFace from '../../assets/images/lion-face.jpg';
import elephantFace from '../../assets/images/elephant-face.jpg';
import wildebeestFace from '../../assets/images/wildebeest-face.jpg';
import rhinoFace from '../../assets/images/rhino-face.jpg';
import wilddogFace from '../../assets/images/wilddog-face.jpg';
import eagleFace from '../../assets/images/eagle-face.jpg';

const NGORONGORO_IMAGES = NGORONGORO_PAGE_IMAGES;

/* ─── Page data ─── */
const HERO_STATS = [
  { value: '260', label: 'km² crater floor', delay: 0 },
  { value: '25,000+', label: 'Large mammals', delay: 80 },
  { value: '610m', label: 'Crater depth', delay: 160 },
  { value: 'UNESCO', label: 'World Heritage Site', delay: 240 },
];

const WILDLIFE = [
  {
    animalFace: lionFace,
    title: 'Crater Lions',
    text: 'Dense lion prides roam the crater floor one of the best places in Africa to see big cats at close range.',
    facePosition: 'center 38%',
  },
  {
    animalFace: rhinoFace,
    title: 'Black Rhinos',
    text: 'Ngorongoro is famed for black rhino sightings,a rare chance to see these endangered giants in open grassland.',
    facePosition: 'center 40%',
  },
  {
    animalFace: elephantFace,
    title: 'Bull Elephants',
    text: 'Magnificent old bulls with impressive tusks move between Lerai Forest and the crater floor year-round.',
    facePosition: 'center 42%',
  },
  {
    animalFace: wildebeestFace,
    title: 'Resident Herds',
    text: 'Wildebeest, zebra, and buffalo graze the caldera in vast numbers a self-contained wildlife world.',
    facePosition: 'center 35%',
  },
  {
    animalFace: wilddogFace,
    title: 'Hippos & Hyenas',
    text: 'Hippo pools at Ngoitoktok Springs and spotted hyenas on the plains add drama to every crater drive.',
    facePosition: 'center 36%',
  },
  {
    animalFace: eagleFace,
    title: 'Flamingos & Birds',
    text: 'Lake Magadi draws flocks of flamingos while raptors and waterbirds thrive across the crater ecosystem.',
    facePosition: 'center 28%',
  },
];

const SEASONS = [
  {
    badge: 'peak',
    title: 'Dry Season',
    months: 'June – October',
    text: 'Clear skies, excellent visibility into the caldera, and wildlife concentrated around water peak crater game drives.',
  },
  {
    badge: 'green',
    title: 'Green Season',
    months: 'November – March',
    text: 'Lush crater walls, fewer vehicles on the floor, and vibrant birdlife around Lake Magadi and forest zones.',
  },
  {
    badge: 'shoulder',
    title: 'Shoulder Season',
    months: 'April – May',
    text: 'Long rains soften the landscape and lower lodge rates. Mornings are often clear with afternoon showers.',
  },
];

const EXPERIENCES = [
  {
    image: NGORONGORO_IMAGES.crater,
    title: 'Crater Floor Game Drive',
    text: 'Descend into the caldera for a full-day safari among lions, rhinos, elephants, and thousands of grazing animals.',
  },
  {
    image: NGORONGORO_IMAGES.rim,
    title: 'Crater Rim Lodges',
    text: 'Stay on the rim for sunrise views over the caldera mist lifting to reveal the entire ecosystem below.',
  },
  {
    image: NGORONGORO_IMAGES.culture,
    title: 'Maasai & Olduvai Gorge',
    text: 'Visit Maasai communities and the cradle of mankind at Olduvai culture and history beside the safari.',
  },
];

const GALLERY_IMAGES = [
  { src: NGORONGORO_IMAGES.hero, caption: 'Ngorongoro Caldera' },
  { src: NGORONGORO_IMAGES.wildlife, caption: 'Crater Wildlife' },
  { src: NGORONGORO_IMAGES.crater, caption: 'Crater Floor' },
  { src: NGORONGORO_IMAGES.rim, caption: 'Crater Rim View' },
  { src: NGORONGORO_IMAGES.overview, caption: 'Savanna Lion' },
  { src: NGORONGORO_IMAGES.culture, caption: 'Maasai Heritage' },
];

const SAFARI_PACKAGES = [
  {
    image: NGORONGORO_IMAGES.crater,
    title: 'Crater Day Safari',
    duration: '1–2 days',
    tag: 'Short safari',
    text: 'Descend into the caldera for a full day among lions, rhinos, and vast herds, with an overnight on the crater rim.',
    highlights: ['Crater descent permit', 'Full-day game drive', 'Rim lodge overnight'],
  },
  {
    image: NGORONGORO_IMAGES.rim,
    title: 'Ngorongoro & Manyara',
    duration: '3–4 days',
    tag: 'Classic combo',
    text: 'Pair Lake Manyara with Ngorongoro for tree-climbing lions, crater wildlife, and comfortable lodges near Karatu.',
    highlights: ['Two parks', 'Private 4×4', 'Karatu / rim stays'],
  },
  {
    image: NGORONGORO_IMAGES.culture,
    title: 'Crater, Culture & Olduvai',
    duration: '4–5 days',
    tag: 'Culture + safari',
    text: 'Combine crater game drives with Maasai encounters and a visit to Olduvai Gorge wildlife, culture, and human origins.',
    highlights: ['Maasai visit', 'Olduvai Gorge', 'Rim viewpoints'],
  },
  {
    image: NGORONGORO_IMAGES.wildlife,
    title: 'Northern Circuit Highlights',
    duration: '6–8 days',
    tag: 'Most popular',
    text: 'Tarangire, Ngorongoro Crater, and the Serengeti in one journey elephants, the caldera, and endless plains.',
    highlights: ['Multi-park itinerary', 'Crater + Serengeti', 'Flexible lodge styles'],
  },
];

const GETTING_THERE_ROUTES = [
  'Drive from Arusha via Karatu (approx. 3–4 hrs) the classic Northern Circuit approach',
  'Combine with Serengeti and Lake Manyara on a multi-park itinerary through the conservation area',
  'Fly into Lake Manyara or Seronera airstrips, then transfer to crater rim lodges',
  'Most visits include a dedicated crater descent permit plan one full day on the floor',
];

/* ─── Lightbox component ─── */
function Lightbox({ image, onClose }) {
  if (!image) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="ngorongoro-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={handleBackdropClick}
    >
      <button
        className="ngorongoro-lightbox__close"
        aria-label="Close image viewer"
        onClick={onClose}
      >
        ✕
      </button>
      <img
        className="ngorongoro-lightbox__img"
        src={image.src}
        alt={image.caption}
      />
      <span className="ngorongoro-lightbox__caption">{image.caption}</span>
    </div>
  );
}

/* ─── Main page component ─── */
function Ngorongoro() {
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="ngorongoro">
      {/* ──── Hero ──── */}
      <section className="ngorongoro-hero" aria-labelledby="ngorongoro-hero-heading">
        <div className="ngorongoro-hero__media" aria-hidden="true">
          <HeroSlideshow
            classPrefix="ngorongoro-hero"
            slides={NGORONGORO_HERO_SLIDES}
            fallback={IMAGE_FALLBACK}
          />
          <div className="ngorongoro-hero__overlay" />
        </div>
        <div className="page-container ngorongoro-hero__container">
          <div className="ngorongoro-hero__content">
            <nav className="ngorongoro-hero__breadcrumb" aria-label="Breadcrumb">
              <a href="/">Home</a>
              <span className="ngorongoro-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <a href="#tanzania-mainland">Mainland</a>
              <span className="ngorongoro-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <span>Ngorongoro</span>
            </nav>
            <p className="ngorongoro-hero__eyebrow">Northern Safari Circuit</p>
            <h1 id="ngorongoro-hero-heading">
              Ngorongoro
              <span>Conservation Area</span>
            </h1>
            <p className="ngorongoro-hero__lead">
              The world&apos;s largest intact volcanic caldera a natural amphitheatre
              where lions, rhinos, and elephants share an ancient crater floor.
            </p>
            <div className="ngorongoro-hero__actions">
              <a className="ngorongoro-btn ngorongoro-btn--primary" href="#contact">
                Plan your crater safari
              </a>
              <a className="ngorongoro-btn ngorongoro-btn--outline" href="#ngorongoro-gallery">
                View gallery
              </a>
            </div>
          </div>
        </div>
        <div className="ngorongoro-hero__stats">
          {HERO_STATS.map((stat) => (
            <div
              key={stat.label}
              className="ngorongoro-hero__stat"
              style={{ '--stat-delay': `${stat.delay}ms` }}
            >
              <span className="ngorongoro-hero__stat-value">{stat.value}</span>
              <span className="ngorongoro-hero__stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ──── Overview ──── */}
      <section
        id="ngorongoro-overview"
        className="ngorongoro-section ngorongoro-overview"
        aria-labelledby="overview-heading"
      >
        <div className="page-container">
          <div className="ngorongoro-overview__grid">
            <Reveal className="ngorongoro-overview__text">
              <p className="ngorongoro-section__eyebrow">About the crater</p>
              <h2 id="overview-heading">Africa&apos;s Eden in a volcano</h2>
              <p>
                The Ngorongoro Conservation Area protects a vast highland landscape
                centred on a spectacular caldera formed when a volcano collapsed
                millions of years ago. Today the crater floor is one of the densest
                wildlife habitats on the continent — often called the Eighth Wonder of
                the World.
              </p>
              <p>
                Unlike a national park, Ngorongoro is a multiple land-use area where
                Maasai pastoralists coexist with wildlife. Descending the steep crater
                walls reveals grasslands, acacia forest, soda lakes, and swamps,
                home to the Big Five and over 500 bird species.
              </p>
              <ul className="ngorongoro-overview__details">
                <Reveal as="li" className="ngorongoro-overview__detail" delay={0}>
                  <span className="ngorongoro-overview__detail-label">Location</span>
                  <span className="ngorongoro-overview__detail-value">Northern Tanzania</span>
                </Reveal>
                <Reveal as="li" className="ngorongoro-overview__detail" delay={80}>
                  <span className="ngorongoro-overview__detail-label">Crater floor</span>
                  <span className="ngorongoro-overview__detail-value">260 km²</span>
                </Reveal>
                <Reveal as="li" className="ngorongoro-overview__detail" delay={160}>
                  <span className="ngorongoro-overview__detail-label">Established</span>
                  <span className="ngorongoro-overview__detail-value">1959</span>
                </Reveal>
                <Reveal as="li" className="ngorongoro-overview__detail" delay={240}>
                  <span className="ngorongoro-overview__detail-label">Best for</span>
                  <span className="ngorongoro-overview__detail-value">Big 5 &amp; Caldera views</span>
                </Reveal>
              </ul>
            </Reveal>
            <Reveal className="ngorongoro-overview__image" variant="right" delay={200}>
              <SafeImage
                className="ngorongoro-overview__img"
                src={NGORONGORO_IMAGES.overview}
                alt="Lion on the African savanna during a safari adventure"
              />
              <span className="ngorongoro-overview__image-accent" aria-hidden="true" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ──── Safari packages ──── */}
      <section
        id="ngorongoro-packages"
        className="ngorongoro-section ngorongoro-packages"
        aria-labelledby="packages-heading"
      >
        <div className="page-container">
          <Reveal className="ngorongoro-section__header">
            <p className="ngorongoro-section__eyebrow">Safari packages</p>
            <h2 id="packages-heading">Ngorongoro safari packages</h2>
            <p className="ngorongoro-section__intro">
              From a single crater day to a full Northern Circuit journey each
              package is tailored to your dates, lodge style, and travel pace.
            </p>
          </Reveal>
          <ul className="ngorongoro-packages__grid">
            {SAFARI_PACKAGES.map((pkg, index) => (
              <Reveal
                key={pkg.title}
                as="li"
                className="ngorongoro-packages__card"
                delay={index * 100}
              >
                <div className="ngorongoro-packages__card-image">
                  <SafeImage src={pkg.image} alt={pkg.title} />
                  <span className="ngorongoro-packages__duration">{pkg.duration}</span>
                </div>
                <div className="ngorongoro-packages__card-body">
                  <span className="ngorongoro-packages__tag">{pkg.tag}</span>
                  <h3>{pkg.title}</h3>
                  <p>{pkg.text}</p>
                  <ul className="ngorongoro-packages__highlights">
                    {pkg.highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <a className="ngorongoro-packages__cta" href="#contact">
                    Enquire about this package →
                  </a>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* ──── Wildlife highlights ──── */}
      <section
        id="ngorongoro-wildlife"
        className="ngorongoro-section ngorongoro-wildlife"
        aria-labelledby="wildlife-heading"
      >
        <div className="page-container">
          <Reveal className="ngorongoro-section__header">
            <p className="ngorongoro-section__eyebrow">Iconic wildlife</p>
            <h2 id="wildlife-heading">Life inside the caldera</h2>
            <p className="ngorongoro-section__intro">
              The crater&apos;s natural walls create a self-contained ecosystem predators,
              grazers, and rare species share a landscape unlike anywhere else on Earth.
            </p>
          </Reveal>
          <ul className="ngorongoro-wildlife__grid">
            {WILDLIFE.map((item, index) => (
              <Reveal
                key={item.title}
                as="li"
                className="ngorongoro-wildlife__card"
                delay={index * 100}
                variant="scale"
              >
                <div className="ngorongoro-wildlife__icon-frame">
                  <span className="ngorongoro-wildlife__icon-ring" aria-hidden="true" />
                  <span
                    className="ngorongoro-wildlife__icon"
                    style={{ '--wildlife-face-position': item.facePosition }}
                  >
                    <img src={item.animalFace} alt={item.title} />
                  </span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* ──── Gallery ──── */}
      <section
        id="ngorongoro-gallery"
        className="ngorongoro-section ngorongoro-gallery"
        aria-labelledby="gallery-heading"
      >
        <div className="page-container">
          <Reveal className="ngorongoro-section__header">
            <p className="ngorongoro-section__eyebrow">Photo gallery</p>
            <h2 id="gallery-heading">Scenes from Ngorongoro</h2>
            <p className="ngorongoro-section__intro">
              From rim-top panoramas to intimate wildlife moments on the crater floor
              the caldera in every light.
            </p>
          </Reveal>
          <div className="ngorongoro-gallery__grid">
            {GALLERY_IMAGES.map((img, index) => (
              <Reveal
                key={img.caption}
                className="ngorongoro-gallery__item"
                delay={index * 100}
                variant="scale"
                onClick={() => setLightboxImage(img)}
                role="button"
                tabIndex={0}
                aria-label={`View ${img.caption}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setLightboxImage(img);
                  }
                }}
              >
                <SafeImage src={img.src} alt={img.caption} />
                <span className="ngorongoro-gallery__caption">{img.caption}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {lightboxImage && (
        <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}

      {/* ──── Best time to visit ──── */}
      <section
        id="ngorongoro-seasons"
        className="ngorongoro-section ngorongoro-seasons"
        aria-labelledby="seasons-heading"
      >
        <div className="page-container">
          <Reveal className="ngorongoro-section__header">
            <p className="ngorongoro-section__eyebrow">Plan your timing</p>
            <h2 id="seasons-heading">Best time to visit Ngorongoro</h2>
            <p className="ngorongoro-section__intro">
              The crater rewards visitors year-round choose your season for clear
              views, green landscapes, or quieter drives.
            </p>
          </Reveal>
          <ul className="ngorongoro-seasons__grid">
            {SEASONS.map((season, index) => (
              <Reveal
                key={season.title}
                as="li"
                className="ngorongoro-seasons__card"
                delay={index * 120}
              >
                <span
                  className={`ngorongoro-seasons__card-badge ngorongoro-seasons__card-badge--${season.badge}`}
                >
                  {season.badge === 'peak'
                    ? '★ Peak Season'
                    : season.badge === 'green'
                      ? '🌿 Green Season'
                      : '◐ Shoulder Season'}
                </span>
                <h3>{season.title}</h3>
                <span className="ngorongoro-seasons__card-months">{season.months}</span>
                <p>{season.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* ──── Experiences ──── */}
      <section
        id="ngorongoro-experiences"
        className="ngorongoro-section ngorongoro-experiences"
        aria-labelledby="experiences-heading"
      >
        <div className="page-container">
          <Reveal className="ngorongoro-section__header">
            <p className="ngorongoro-section__eyebrow">Safari experiences</p>
            <h2 id="experiences-heading">Ways to explore Ngorongoro</h2>
            <p className="ngorongoro-section__intro">
              From crater-floor game drives to cultural encounters every experience
              reveals a different face of this UNESCO World Heritage site.
            </p>
          </Reveal>
          <ul className="ngorongoro-experiences__grid">
            {EXPERIENCES.map((exp, index) => (
              <Reveal
                key={exp.title}
                as="li"
                className="ngorongoro-experiences__card"
                delay={index * 120}
              >
                <div className="ngorongoro-experiences__card-image">
                  <SafeImage src={exp.image} alt={exp.title} />
                </div>
                <div className="ngorongoro-experiences__card-body">
                  <h3>{exp.title}</h3>
                  <p>{exp.text}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* ──── Getting there ──── */}
      <section
        id="ngorongoro-getting-there"
        className="ngorongoro-section ngorongoro-getting-there"
        aria-labelledby="getting-there-heading"
      >
        <div className="page-container">
          <div className="ngorongoro-getting-there__inner">
            <Reveal className="ngorongoro-getting-there__copy">
              <p className="ngorongoro-section__eyebrow">Getting there</p>
              <h2 id="getting-there-heading">How to reach Ngorongoro</h2>
              <p>
                Ngorongoro sits between Arusha and the Serengeti on Tanzania&apos;s
                Northern Circuit. Most safaris include at least one night on the
                crater rim and a full day descending to the floor.
              </p>
              <ul className="ngorongoro-getting-there__routes">
                {GETTING_THERE_ROUTES.map((route) => (
                  <li key={route}>{route}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="ngorongoro-getting-there__map" variant="right" delay={200}>
              <iframe
                title="Ngorongoro Conservation Area location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d255168.089654321!2d35.45!3d-3.2!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x18371e2348a4c813%3A0x7c1e3a5e5b5e5b5e!2sNgorongoro%20Conservation%20Area!5e0!3m2!1sen!2stz!4v1700000000000!5m2!1sen!2stz"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ──── CTA ──── */}
      <section className="ngorongoro-cta" aria-labelledby="ngorongoro-cta-heading">
        <div className="page-container">
          <Reveal variant="scale" delay={0}>
            <div className="ngorongoro-cta__inner">
              <h2 id="ngorongoro-cta-heading">
                Ready to descend into the crater?
              </h2>
              <p>
                Tell us your travel dates we&apos;ll design a tailor-made
                Ngorongoro safari with rim lodges, crater drives, and Northern
                Circuit connections.
              </p>
              <a
                className="ngorongoro-btn ngorongoro-btn--primary ngorongoro-btn--lg"
                href="#contact"
              >
                Get a free Ngorongoro quote
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export default Ngorongoro;
